import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AddNewRegistrationPayload,
  CourseHistoryResponse,
  GenerateRegistrationLinkPayload,
  GenerateRegistrationLinkResponse,
  ListRegistrationFilter,
  ListRegistrationFilterAct,
  ListRegistrationResponse,
  RegistrationData,
  RegistrationResumeResponse,
  ResumeStatusFilterKey,
  ResumeDrilldownFilter,
  ResumeDrilldownResponse,
  StudentData,
} from 'src/app/class/datatype';
import { environment } from 'src/environments/environment';

@Injectable()
export class RegistrationService {
  constructor(private http: HttpClient) {}

  /**
   * Daftarkan siswa baru dari panel.
   *
   * Menembak `clientapi/registration` (RegistrationController@storeFromPanel)
   * memakai auth panel yang sama dengan menu lain — tidak lagi lewat service
   * eksternal add-new-registration-be.
   */
  storeFromPanel(payload: AddNewRegistrationPayload): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + 'clientapi/registration',
      { data: payload }
    );
  }

  /**
   * Generate link pendaftaran bertoken untuk CS (Assisted Registration).
   */
  generateRegistrationLink(
    payload: GenerateRegistrationLinkPayload
  ): Observable<GenerateRegistrationLinkResponse> {
    return this.http.post<GenerateRegistrationLinkResponse>(
      environment.apiUrl + 'clientapi/registration/generate-link',
      { data: payload }
    );
  }

  listRegistration(
    filter: ListRegistrationFilter,
    filterAct: ListRegistrationFilterAct
  ): Observable<ListRegistrationResponse> {
    let params = new HttpParams();

    if (filter.regcode && filterAct.regcode) {
      params = params.append('regcode', filter.regcode.toString());
    }

    if (filter.branch && filterAct.branch) {
      params = params.append('branch', filter.branch.toString());
    }

    if (filter.phone && filterAct.phone) {
      params = params.append('phone', filter.phone.toString());
    }

    if (filter.name && filterAct.name) {
      params = params.append('name', filter.name.toString());
    }

    if (filter.gender && filterAct.gender) {
      params = params.append('gender', filter.gender.toString());
    }

    if (filter.email && filterAct.email) {
      params = params.append('email', filter.email.toString());
    }

    if (filter.course && filterAct.course) {
      params = params.append('course', filter.course.toString());
    }

    if (filter.is_partial && filterAct.is_partial) {
      params = params.append('is_partial', filter.is_partial.toString());
    }

    if (filter.periode && filterAct.periode) {
      params = params.append('periode_date', filter.periode.date.toString());
      params = params.append('periode_month', filter.periode.month.toString());

      if (filter.periode.year) {
        params = params.append('periode_year', filter.periode.year.toString());
      }
    }

    if (filter.down_payment && filterAct.down_payment) {
      params = params.append('down_payment', filter.down_payment.toString());
    }

    if (filter.status && filterAct.status) {
      params = params.append('status', filter.status.toString());
    }

    if (filter.lead && filterAct.lead) {
      params = params.append('lead', filter.lead.toString());
    }

    if (filter.aff && filterAct.aff) {
      params = params.append('aff', filter.aff.toString());
    }

    if (filter.utm_source && filterAct.utm_source) {
      params = params.append('utm_source', filter.utm_source.toString());
    }

    if (filter.cs && filterAct.cs) {
      params = params.append('cs', filter.cs.toString());
    }

    if (filter.education && filterAct.education) {
      params = params.append('education', filter.education.toString());
    }

    if (filter.tshirt && filterAct.tshirt) {
      params = params.append('tshirt', filter.tshirt.toString());
    }

    if (filter.pickup && filterAct.pickup) {
      params = params.append('pickup', filter.pickup.toString());
    }

    if (filter.passenger && filterAct.passenger) {
      params = params.append('passenger', filter.passenger);
    }

    if (filter.pickupLocation && filterAct.pickupLocation) {
      params = params.append(
        'pickupLocation',
        filter.pickupLocation.toString()
      );
    }

    if (filter.pickupTime && filterAct.pickupTime) {
      params = params.append('pickupTime', filter.pickupTime.toString());
    }

    if (filter.startDate && filterAct.startDate) {
      params = params.append('startDate', filter.startDate.toString());
    }

    if (filter.endDate && filterAct.endDate) {
      params = params.append('endDate', filter.endDate.toString());
    }

    if (filter.extends_program && filterAct.extends_program) {
      params = params.append('is_retention', filter.extends_program);
    }

    if (filter.is_ac && filterAct.is_ac) {
      params = params.append('is_ac', filter.is_ac.toString());
    }

    if (filter.voucher && filterAct.voucher) {
      params = params.append('voucher', filter.voucher.toString());
    }

    if (filter.grade && filterAct.grade) {
      params = params.append('grade_id', filter.grade.toString());
    }

    if (filter.meet_hour_id && filterAct.meet_hour_id) {
      params = params.append('meet_hour_id', filter.meet_hour_id.toString());
    }

    if (filter.label_id && filterAct.label) {
      params = params.append('label_id', filter.label_id.toString());
    }

    if (filter.is_returning && filterAct.is_returning) {
      params = params.append('is_returning', filter.is_returning);
    }

    params = params.append('page', filter.page.toString());
    params = params.append('per_page', filter.per_page.toString());

    return this.http.get<ListRegistrationResponse>(
      environment.apiUrl + 'clientapi/registration',
      { params }
    );
  }

  exportRegistration(
    filter: ListRegistrationFilter,
    filterAct: ListRegistrationFilterAct,
    format: 'csv' | 'xlsx' = 'csv'
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();

    if (filter.regcode && filterAct.regcode) {
      params = params.append('regcode', filter.regcode.toString());
    }

    if (filter.branch && filterAct.branch) {
      params = params.append('branch', filter.branch.toString());
    }

    if (filter.name && filterAct.name) {
      params = params.append('name', filter.name.toString());
    }

    if (filter.gender && filterAct.gender) {
      params = params.append('gender', filter.gender.toString());
    }

    if (filter.email && filterAct.email) {
      params = params.append('email', filter.email.toString());
    }

    if (filter.phone && filterAct.phone) {
      params = params.append('phone', filter.phone.toString());
    }

    if (filter.course && filterAct.course) {
      params = params.append('course', filter.course.toString());
    }

    if (filter.is_partial && filterAct.is_partial) {
      params = params.append('is_partial', filter.is_partial.toString());
    }

    if (filter.periode && filterAct.periode) {
      params = params.append('periode_date', filter.periode.date.toString());
      params = params.append('periode_month', filter.periode.month.toString());

      if (filter.periode.year) {
        params = params.append('periode_year', filter.periode.year.toString());
      }
    }

    if (filter.grade && filterAct.grade) {
      params = params.append('grade_id', filter.grade.toString());
    }

    if (filter.meet_hour_id && filterAct.meet_hour_id) {
      params = params.append('meet_hour_id', filter.meet_hour_id.toString());
    }

    if (filter.label_id && filterAct.label) {
      params = params.append('label_id', filter.label_id.toString());
    }

    if (filter.down_payment && filterAct.down_payment) {
      params = params.append('down_payment', filter.down_payment.toString());
    }

    if (filter.status && filterAct.status) {
      params = params.append('status', filter.status.toString());
    }

    if (filter.lead && filterAct.lead) {
      params = params.append('lead', filter.lead.toString());
    }

    if (filter.aff && filterAct.aff) {
      params = params.append('aff', filter.aff.toString());
    }

    if (filter.utm_source && filterAct.utm_source) {
      params = params.append('utm_source', filter.utm_source.toString());
    }

    if (filter.cs && filterAct.cs) {
      params = params.append('cs', filter.cs.toString());
    }

    if (filter.pickup && filterAct.pickup) {
      params = params.append('pickup', filter.pickup.toString());
    }

    if (filter.pickupLocation && filterAct.pickupLocation) {
      params = params.append(
        'pickupLocation',
        filter.pickupLocation.toString()
      );
    }

    if (filter.pickupTime && filterAct.pickupTime) {
      params = params.append('pickupTime', filter.pickupTime.toString());
    }

    if (filter.startDate && filterAct.startDate) {
      params = params.append('startDate', filter.startDate.toString());
    }

    if (filter.endDate && filterAct.endDate) {
      params = params.append('endDate', filter.endDate.toString());
    }

    if (filter.is_returning && filterAct.is_returning) {
      params = params.append('is_returning', filter.is_returning);
    }

    params = params.append('format', format);

    return this.http.get(
      environment.apiUrl + 'clientapi/registration/download',
      {
        params,
        responseType: 'blob',
        observe: 'response',
      }
    );
  }

  listRegistrationDownload(
    filter: ListRegistrationFilter,
    filterAct: ListRegistrationFilterAct
  ): Observable<ListRegistrationResponse> {
    let params = new HttpParams();

    if (filter.regcode && filterAct.regcode) {
      params = params.append('regcode', filter.regcode.toString());
    }

    if (filter.branch && filterAct.branch) {
      params = params.append('branch', filter.branch.toString());
    }

    if (filter.name && filterAct.name) {
      params = params.append('name', filter.name.toString());
    }

    if (filter.gender && filterAct.gender) {
      params = params.append('gender', filter.gender.toString());
    }

    if (filter.email && filterAct.email) {
      params = params.append('email', filter.email.toString());
    }

    if (filter.course && filterAct.course) {
      params = params.append('course', filter.course.toString());
    }

    if (filter.is_partial && filterAct.is_partial) {
      params = params.append('is_partial', filter.is_partial.toString());
    }

    if (filter.periode && filterAct.periode) {
      params = params.append('periode_date', filter.periode.date.toString());
      params = params.append('periode_month', filter.periode.month.toString());

      if (filter.periode.year) {
        params = params.append('periode_year', filter.periode.year.toString());
      }
    }

    if (filter.grade && filterAct.grade) {
      params = params.append('grade_id', filter.grade.toString());
    }

    if (filter.meet_hour_id && filterAct.meet_hour_id) {
      params = params.append('meet_hour_id', filter.meet_hour_id.toString());
    }

    if (filter.label_id && filterAct.label) {
      params = params.append('label_id', filter.label_id.toString());
    }

    if (filter.down_payment && filterAct.down_payment) {
      params = params.append('down_payment', filter.down_payment.toString());
    }

    if (filter.status && filterAct.status) {
      params = params.append('status', filter.status.toString());
    }

    if (filter.lead && filterAct.lead) {
      params = params.append('lead', filter.lead.toString());
    }

    if (filter.aff && filterAct.aff) {
      params = params.append('aff', filter.aff.toString());
    }

    if (filter.utm_source && filterAct.utm_source) {
      params = params.append('utm_source', filter.utm_source.toString());
    }

    if (filter.cs && filterAct.cs) {
      params = params.append('cs', filter.cs.toString());
    }

    if (filter.pickup && filterAct.pickup) {
      params = params.append('pickup', filter.pickup.toString());
    }

    if (filter.pickupLocation && filterAct.pickupLocation) {
      params = params.append(
        'pickupLocation',
        filter.pickupLocation.toString()
      );
    }

    if (filter.pickupTime && filterAct.pickupTime) {
      params = params.append('pickupTime', filter.pickupTime.toString());
    }

    if (filter.startDate && filterAct.startDate) {
      params = params.append('startDate', filter.startDate.toString());
    }

    if (filter.endDate && filterAct.endDate) {
      params = params.append('endDate', filter.endDate.toString());
    }

    params = params.append('page', filter.page.toString());
    params = params.append('per_page', filter.per_page.toString());

    return this.http.get<ListRegistrationResponse>(
      environment.apiUrl + 'clientapi/registration/download',
      { params }
    );
  }

  listRetentionDownload(
    filter: ListRegistrationFilter,
    filterAct: ListRegistrationFilterAct
  ): Observable<ListRegistrationResponse> {
    let params = new HttpParams();

    if (filter.regcode && filterAct.regcode) {
      params = params.append('regcode', filter.regcode.toString());
    }

    if (filter.branch && filterAct.branch) {
      params = params.append('branch', filter.branch.toString());
    }

    if (filter.name && filterAct.name) {
      params = params.append('name', filter.name.toString());
    }

    if (filter.gender && filterAct.gender) {
      params = params.append('gender', filter.gender.toString());
    }

    if (filter.email && filterAct.email) {
      params = params.append('email', filter.email.toString());
    }

    if (filter.course && filterAct.course) {
      params = params.append('course', filter.course.toString());
    }

    if (filter.is_partial && filterAct.is_partial) {
      params = params.append('is_partial', filter.is_partial.toString());
    }

    if (filter.periode && filterAct.periode) {
      params = params.append('periode_date', filter.periode.date.toString());
      params = params.append('periode_month', filter.periode.month.toString());

      if (filter.periode.year) {
        params = params.append('periode_year', filter.periode.year.toString());
      }
    }

    if (filter.down_payment && filterAct.down_payment) {
      params = params.append('down_payment', filter.down_payment.toString());
    }

    if (filter.status && filterAct.status) {
      params = params.append('status', filter.status.toString());
    }

    if (filter.lead && filterAct.lead) {
      params = params.append('lead', filter.lead.toString());
    }

    if (filter.aff && filterAct.aff) {
      params = params.append('aff', filter.aff.toString());
    }

    if (filter.utm_source && filterAct.utm_source) {
      params = params.append('utm_source', filter.utm_source.toString());
    }

    if (filter.cs && filterAct.cs) {
      params = params.append('cs', filter.cs.toString());
    }

    if (filter.pickup && filterAct.pickup) {
      params = params.append('pickup', filter.pickup.toString());
    }

    if (filter.pickupLocation && filterAct.pickupLocation) {
      params = params.append(
        'pickupLocation',
        filter.pickupLocation.toString()
      );
    }

    if (filter.pickupTime && filterAct.pickupTime) {
      params = params.append('pickupTime', filter.pickupTime.toString());
    }

    if (filter.startDate && filterAct.startDate) {
      params = params.append('startDate', filter.startDate.toString());
    }

    if (filter.endDate && filterAct.endDate) {
      params = params.append('endDate', filter.endDate.toString());
    }

    params = params.append('is_retention', 1);
    params = params.append('page', filter.page.toString());
    params = params.append('per_page', filter.per_page.toString());

    return this.http.get<ListRegistrationResponse>(
      environment.apiUrl + 'clientapi/registration/retention/download',
      { params }
    );
  }

  saveRegistration(data: RegistrationData): Observable<any> {
    return this.http.put(environment.apiUrl + 'clientapi/registration', data);
  }

  updateCourse(data: RegistrationData): Observable<any> {
    return this.http.put(
      environment.apiUrl + 'clientapi/registration/update-course',
      data
    );
  }

  updateCustomerService(data: RegistrationData): Observable<any> {
    return this.http.put(
      environment.apiUrl + 'clientapi/registration/update-cs',
      data
    );
  }

  getRegistrationURL(data: RegistrationData): Observable<any> {
    let params = new HttpParams();

    params = params.append('regcode', data.regcode.toString());
    params = params.append('email', data.email.toString());
    params = params.append('status', data.status.toString());

    return this.http.get(
      environment.apiUrl + 'clientapi/registration/get-url-biodata',
      { params }
    );
  }

  GenLinkFullPayData(data) {
    return this.http.post(
      environment.apiUrl + 'clientapi/registration/get-url-full-pay',
      data
    );
  }

  getRegistrationData(data: RegistrationData): Observable<any> {
    let params = new HttpParams();

    params = params.append('regcode', data.regcode.toString());
    params = params.append('email', data.email.toString());
    params = params.append('status', data.status.toString());

    return this.http.get(
      environment.apiUrl + 'clientapi/registration/get-member-biodata',
      { params }
    );
  }

  genFullPayLink(data) {
    let params = new HttpParams();

    return this.http.get(
      environment.apiUrl + 'clientapi/registration/get-member-biodata',
      { params }
    );
  }

  saveCamp(data: {
    student_id: number;
    camp_id: number;
  }): Observable<StudentData> {
    return this.http.post<StudentData>(
      environment.apiUrl + 'clientapi/registration/camp',
      data
    );
  }

  getRegistration(regcode: string): Observable<RegistrationData> {
    let params = new HttpParams();

    params = params.append('regcode', regcode);

    return this.http.get<RegistrationData>(
      environment.apiUrl + 'clientapi/registration/get',
      { params }
    );
  }

  /**
   * Riwayat kursus siswa lama (alumni): registrasi sebelumnya milik orang
   * yang sama dari semua cabang (RegistrationController@courseHistory).
   */
  getCourseHistory(regcode: string): Observable<CourseHistoryResponse> {
    let params = new HttpParams();

    params = params.append('regcode', regcode);

    return this.http.get<CourseHistoryResponse>(
      environment.apiUrl + 'clientapi/registration/course-history',
      { params }
    );
  }

  getPeriodeHasRegistration(all = false): Observable<string[]> {
    let params = new HttpParams();

    if (all) {
      params = params.append('all', '1');
    }

    return this.http.get<string[]>(
      environment.apiUrl +
        'clientapi/registration-report/periode-has-registration',
      { params }
    );
  }

  getCourseHasRegistration(
    periode: string
  ): Observable<{ course: string; course_name: string }[]> {
    let params = new HttpParams();

    params = params.append('periode', periode);

    return this.http.get<{ course: string; course_name: string }[]>(
      environment.apiUrl +
        'clientapi/registration-report/course-has-registration',
      { params }
    );
  }

  getRegistrationReport(
    periode: string,
    course?: string,
    gender?: string
  ): Observable<StudentData[]> {
    let params = new HttpParams();

    params = params.append('periode', periode);

    if (course) {
      params = params.append('course', course);
    }

    if (gender) {
      params = params.append('gender', gender);
    }

    params = params.append('resume', '0');

    return this.http.get<StudentData[]>(
      environment.apiUrl + 'clientapi/registration-report/registration',
      { params }
    );
  }
  
  getRegistrationReports(
    periode: string,
    course?: string,
    gender?: string
  ): Observable<StudentData[]> {
    let params = new HttpParams();

    params = params.append('periode', periode);

    if (course) {
      params = params.append('course', course);
    }

    if (gender) {
      params = params.append('gender', gender);
    }

    params = params.append('resume', '0');

    return this.http.get<StudentData[]>(
      environment.apiUrl + 'clientapi/registration-report',
      { params }
    );
  }

  getRegistrationResume(
    periode: string,
    branch?: string,
    bucket?: ResumeStatusFilterKey
  ): Observable<RegistrationResumeResponse> {
    let params = new HttpParams();

    params = params.append('periode', periode);

    if (branch) {
      params = params.append('branch', branch);
    }

    if (bucket) {
      params = params.append('bucket', bucket);
    }

    return this.http.get<RegistrationResumeResponse>(
      environment.apiUrl + 'clientapi/registration-report/resume',
      { params }
    );
  }

  getRegistrationResumeStudents(
    filter: ResumeDrilldownFilter
  ): Observable<ResumeDrilldownResponse> {
    let params = new HttpParams();

    params = params.append('periode', filter.periode);

    if (filter.branch) {
      params = params.append('branch', filter.branch);
    }

    if (filter.bucket) {
      params = params.append('bucket', filter.bucket);
    }

    if (filter.course) {
      params = params.append('course', filter.course);
    }

    if (filter.gender) {
      params = params.append('gender', filter.gender);
    }

    if (filter.grade_id !== undefined && filter.grade_id !== null) {
      params = params.append('grade_id', filter.grade_id.toString());
    }

    if (filter.jenjang) {
      params = params.append('jenjang', filter.jenjang);
    }

    params = params.append('page', (filter.page ?? 1).toString());
    params = params.append('per_page', (filter.per_page ?? 25).toString());

    return this.http.get<ResumeDrilldownResponse>(
      environment.apiUrl + 'clientapi/registration-report/resume/students',
      { params }
    );
  }

  processCheckin(student: RegistrationData) {
    return this.http.post(
      environment.apiUrl + 'clientapi/registration/checkin',
      {
        regcode: student.regcode,
      }
    );
  }

  removeVoucher(studentId: number): Observable<any> {
    return this.http.put(
      environment.apiUrl + `clientapi/student/${studentId}/remove-voucher`,
      {}
    );
  }

  addVoucher(studentId: number, code: string): Observable<any> {
    return this.http.put(
      environment.apiUrl + `clientapi/student/${studentId}/add-voucher`,
      { code }
    );
  }

  reschedulePeriode(data: {
    student_id: number;
    periode_date: number;
    periode_month: number;
    periode_year: number;
    reason?: string;
    keep_voucher?: boolean;
  }): Observable<any> {
    return this.http.put(
      environment.apiUrl + 'clientapi/registration/reschedule-periode',
      data
    );
  }
}

